/**
 * PhoneMessaging.tsx
 *
 * SMS messaging app for sending and receiving text messages.
 * Phase 3: Skeleton with basic UI ✅
 * Phase 5: Full functionality with WebSocket integration ✅
 *
 * Features:
 * - Message thread UI (chat bubbles)
 * - Sent messages on right, received on left
 * - Compose area with character counter (160 limit)
 * - Send button with WebSocket integration
 * - Delivery status indicators (Sending ⏳ → Sent ✓ → Delivered ✓✓)
 * - Timestamps on messages
 * - Real-time incoming SMS handling
 */

import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, Input } from '@/components/ui';
import { useUserTimezone } from '@/hooks/useDateFormat';
import phoneWebSocketService from '@/services/phoneWebSocketService';
import type { PhoneSmsStatusUpdate, PhoneIncomingSmsUpdate } from '@/services/phoneWebSocketService';

export interface PhoneMessagingProps {
  onBack: () => void;
  disabled?: boolean;
}

interface Message {
  id: string;
  type: 'sent' | 'received';
  text: string;
  timestamp: Date;
  status?: 'sending' | 'sent' | 'delivered' | 'failed';
  /** Sender number for received (MT) messages — shown above the bubble so you
   *  can tell which number an inbound text came from. */
  from?: string;
}

/**
 * Message Bubble Component
 */
interface MessageBubbleProps {
  message: Message;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isSent = message.type === 'sent';
  const tz = useUserTimezone();

  return (
    <div className={cn('flex', isSent ? 'justify-end' : 'justify-start')}>
      <div className="flex flex-col max-w-[70%]">
        {/* Sender number (received messages only) */}
        {!isSent && (
          <span className="text-xs font-medium text-muted-foreground mb-0.5 ml-1">
            {message.from || 'Unknown sender'}
          </span>
        )}

        {/* Message bubble */}
        <div
          className={cn(
            'px-4 py-2 rounded-2xl',
            isSent
              ? 'bg-tech-5g-bg text-tech-5g rounded-br-sm'
              : 'bg-muted text-foreground rounded-bl-sm'
          )}
        >
          <p className="text-sm">{message.text}</p>
        </div>

        {/* Timestamp and status */}
        <div
          className={cn(
            'flex items-center gap-1 mt-1 text-xs text-muted-foreground',
            isSent ? 'justify-end' : 'justify-start'
          )}
        >
          <span>{message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: tz })}</span>
          {isSent && message.status === 'sending' && <span>⏳</span>}
          {isSent && message.status === 'sent' && <span>✓</span>}
          {isSent && message.status === 'delivered' && <span>✓✓</span>}
          {isSent && message.status === 'failed' && <span className="text-status-error">!</span>}
        </div>
      </div>
    </div>
  );
};

/**
 * PhoneMessaging Component (Full WebSocket Integration - Phase 5)
 */
export const PhoneMessaging: React.FC<PhoneMessagingProps> = ({ onBack, disabled = false }) => {
  const [recipientNumber, setRecipientNumber] = useState<string>('');
  const [messageText, setMessageText] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [ownNumber, setOwnNumber] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pendingMessageRef = useRef<string | null>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Seed the thread with the session's persisted history so messages that
  // arrived while this app was CLOSED still display (issue #166) — the live
  // phone_incoming_sms events only reach a mounted listener. De-dup against
  // live bubbles by id (sent bubbles share their smsId with the doc) and,
  // for incoming, by body + close timestamp (live ids are synthetic).
  useEffect(() => {
    let cancelled = false;
    phoneWebSocketService.loadSmsHistory().then((history) => {
      if (cancelled || history.length === 0) return;
      setMessages(prev => {
        const merged: Message[] = [...prev];
        for (const h of history) {
          const hMsg: Message = h.direction === 'incoming'
            ? { id: h.id, type: 'received', text: h.body, timestamp: h.createdAt, from: h.counterparty || undefined }
            : {
                id: h.id, type: 'sent', text: h.body, timestamp: h.createdAt,
                status: (h.status === 'delivered' || h.status === 'failed') ? h.status : 'sent',
              };
          const dup = merged.some(m =>
            m.id === hMsg.id ||
            (m.type === hMsg.type && m.text === hMsg.text &&
             Math.abs(m.timestamp.getTime() - hMsg.timestamp.getTime()) < 10000));
          if (!dup) merged.push(hMsg);
        }
        merged.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        return merged;
      });
    }).catch((err) => console.warn('[PhoneMessaging] SMS history load failed:', err));
    return () => { cancelled = true; };
  }, []);

  // Listen for SMS status updates from WebSocket
  useEffect(() => {
    const handleSmsStatus = (data: PhoneSmsStatusUpdate) => {
      console.log('[PhoneMessaging] Received SMS status:', data);

      // Update message status based on messageId or most recent pending message
      setMessages(prev =>
        prev.map(msg => {
          if (msg.type === 'sent' && msg.status !== 'delivered' && msg.status !== 'failed') {
            // Update if messageId matches, or if it's the most recent pending message
            if (data.messageId && msg.id === data.messageId) {
              return { ...msg, status: data.status };
            } else if (!data.messageId && msg.id === pendingMessageRef.current) {
              return { ...msg, status: data.status };
            }
          }
          return msg;
        })
      );

      // Clear pending reference once delivered or failed
      if (data.status === 'delivered' || data.status === 'failed') {
        pendingMessageRef.current = null;
      }
    };

    const handleIncomingSms = (data: PhoneIncomingSmsUpdate) => {
      console.log('[PhoneMessaging] Received incoming SMS:', data);

      const newMessage: Message = {
        id: `incoming-${Date.now()}`,
        type: 'received',
        text: data.message,
        timestamp: new Date(data.timestamp),
        from: data.from || undefined,
      };

      setMessages(prev => [...prev, newMessage]);
    };

    const handleNetworkStatus = (data: any) => {
      if (data && 'ownNumber' in data) setOwnNumber(data.ownNumber ?? null);
    };

    phoneWebSocketService.on('phone_sms_status', handleSmsStatus);
    phoneWebSocketService.on('phone_incoming_sms', handleIncomingSms);
    phoneWebSocketService.on('phone_network_status', handleNetworkStatus);

    return () => {
      phoneWebSocketService.off('phone_sms_status', handleSmsStatus);
      phoneWebSocketService.off('phone_incoming_sms', handleIncomingSms);
      phoneWebSocketService.off('phone_network_status', handleNetworkStatus);
    };
  }, []);

  const handleSend = () => {
    if (messageText.trim() && recipientNumber.trim() && !disabled) {
      try {
        // The bubble id IS the smsId, so status updates (which carry
        // messageId = smsId) correlate to this bubble across
        // sending → sent → delivered → failed.
        const messageId = `tlam_sms_${crypto.randomUUID()}`;

        // Add message to UI immediately with "sending" status
        const newMessage: Message = {
          id: messageId,
          type: 'sent',
          text: messageText.trim(),
          timestamp: new Date(),
          status: 'sending'
        };

        setMessages(prev => [...prev, newMessage]);
        setMessageText('');

        // Track this message as pending
        pendingMessageRef.current = messageId;

        // Send SMS via the interactive TLAM endpoint (passing our smsId).
        phoneWebSocketService.sendSms(recipientNumber, newMessage.text, messageId);

        console.log('[PhoneMessaging] Sent SMS to:', recipientNumber, newMessage.text);
      } catch (error) {
        console.error('[PhoneMessaging] Error sending SMS:', error);

        // Mark message as failed
        setMessages(prev =>
          prev.map(msg =>
            msg.id === pendingMessageRef.current
              ? { ...msg, status: 'failed' as const }
              : msg
          )
        );
        pendingMessageRef.current = null;
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // GSM 7-bit default alphabet (TS 23.038) incl. the extension table.
  // Anything outside it (Hebrew, Arabic, Cyrillic, CJK, emoji, …) forces a
  // UCS2 send on the edge, where a single segment is 70 UTF-16 code units,
  // not 160 — mirror that here so the counter matches what the modem accepts.
  const GSM7_RE = /^[A-Za-z0-9@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./:;<=>?¡ÄÖÑܧ¿äöñüà\n\r€[\]{}\\|^~]*$/;
  const isUcs2 = !GSM7_RE.test(messageText);
  // Surrogate pairs (emoji) are 1 code point but 2 UTF-16 units — count units.
  const characterCount = isUcs2 ? [...messageText].reduce((n, c) => n + (c.codePointAt(0)! > 0xffff ? 2 : 1), 0) : messageText.length;
  const characterLimit = isUcs2 ? 70 : 160;
  const isOverLimit = characterCount > characterLimit;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex flex-col border-b border-border">
        <div className="flex items-center p-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-muted rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <h2 className="ml-4 text-lg font-semibold text-foreground">Messages</h2>
        </div>

        {/* Your number — tester texts this from their own phone to test receive */}
        <div className="px-4 pb-2 text-xs text-muted-foreground">
          {ownNumber ? (
            <>Your number: <span className="font-medium text-foreground">{ownNumber}</span> — text this from your phone to test receiving</>
          ) : (
            <>Your number: unknown</>
          )}
        </div>

        {/* Recipient number */}
        <div className="px-4 pb-4">
          <Input
            type="tel"
            value={recipientNumber}
            onChange={(e) => setRecipientNumber(e.target.value)}
            placeholder="Recipient number"
            disabled={disabled}
            className="text-sm"
          />
        </div>
      </div>

      {/* Message thread */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <p>No messages yet</p>
              <p className="text-sm mt-1">Send a message to get started</p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Compose area */}
      <div className="border-t border-border p-4">
        {/* Character counter */}
        {messageText && (
          <div className="flex justify-end mb-2">
            <span
              className={cn(
                'text-xs',
                isOverLimit ? 'text-status-error' : 'text-muted-foreground'
              )}
            >
              {characterCount}/{characterLimit}
            </span>
          </div>
        )}

        {/* Input and send button */}
        <div className="flex gap-2">
          <Input
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            disabled={disabled}
            className="flex-1"
            maxLength={characterLimit}
          />
          <Button
            onClick={handleSend}
            disabled={!messageText.trim() || !recipientNumber.trim() || disabled || isOverLimit}
            className="bg-tech-5g-bg text-tech-5g hover:bg-tech-5g-bg/80"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PhoneMessaging;
